import { useCallback } from 'react';
import { Grid } from 'antd';

const { useBreakpoint } = Grid;

/**
 * Widest a pinned identity column may be once the viewport is phone-sized.
 *
 * At 390px the page wrapper leaves roughly 358px of table, so 132px is about
 * 37% of the scroller — under the ~45% ceiling that keeps a pinned lane from
 * eating the data it is supposed to give context to. Above `sm` the caller's
 * own width applies unchanged, because there the lane is a small fraction of
 * a much wider table.
 */
const NARROW_MAX = 132;

/**
 * `fixed: 'left'` for the column(s) that tell a reader which row they are on.
 *
 * Backlog item 16: on a table wide enough to scroll sideways, the identity
 * column scrolls away with everything else and the remaining cells become
 * anonymous. Pinning it keeps the answer to "which row is this?" on screen.
 *
 * Usage — spread it into the LEFTMOST column(s) of the table:
 *
 *   const pinLeft = useStickyIdentity();
 *   const columns = [
 *     { title: 'Reference', dataIndex: 'reference', ...pinLeft(180) },
 *     { title: 'Employee',  dataIndex: 'employee',  ...pinLeft(200, { secondary: true }) },
 *     ...
 *   ];
 *
 * Pass `{ secondary: true }` on the SECOND of two pinned columns. Below `sm`
 * it gives up its pin entirely rather than shrink: two capped lanes would be
 * 264 of the ~358px available at 390px — 74%, which buries the data the pin
 * exists to contextualise. One lane and a legible table beats two lanes and a
 * peephole.
 *
 * Only ever mark the SECOND column secondary, never the identity one. A
 * fixed-left run must start at index 0, so a pinned column at index 1 with an
 * unpinned index 0 sets rc-table's `hasGapFixed`
 * (`useColumns/index.js:238-256`) and antd then forces `box-shadow: none` on
 * every fixed cell (`table/style/fixed.js:110-119`) — a lane that overlaps the
 * scrolled content with no visible edge. Where the identity column is not
 * already leftmost, MOVE it rather than reaching past a passenger.
 *
 * Pass `{ maxWidth }` where the table's scroller is much narrower than the
 * viewport — the breakpoint below reads the window, so a grid inside a
 * slide-over (which reserves ~225px for the sidebar) would otherwise keep a
 * full-width lane in a container that never had the room for it.
 *
 * ## Rules this encodes, and why
 *
 * - **Identity only, at most two columns.** A pinned lane costs horizontal
 *   room permanently; spend it on what names the row (employee, reference, PO
 *   number, item, warehouse), never on an action or a status.
 * - **Leftmost columns, contiguously.** rc-table requires the fixed-left run
 *   to start at index 0 — a gap makes antd warn and drops the shadow via
 *   `ant-table-fixed-column-gapped`. Where `expandable` inserts its own icon
 *   column, that column copies `fixed` off the column it displaces
 *   (`rc-table/lib/hooks/useColumns/index.js`), so pinning the first data
 *   column pins the expand icon with it — nothing extra to configure.
 * - **An explicit width is required.** rc-table emits the body `<colgroup>`
 *   from the columns' *declared* widths (`Table.js:503-509`), so a pinned
 *   column with no width has no `<col>` to size it.
 *
 * ## What it does NOT need
 *
 * A numeric `scroll.x` and `tableLayout="fixed"` are NOT preconditions. A
 * `fixed` column plus any valid `scroll.x` — `'max-content'` included —
 * already selects fixed layout (`rc-table/lib/Table.js:457-474`), and under
 * `'max-content'` rc-table deliberately stays on `auto` layout while still
 * measuring real column widths for the sticky offsets
 * (`useStickyOffsets.js`, fed by the `MeasureRow` that `horizonScroll` turns
 * on). Verified in a browser: `scroll={{ x: 'max-content' }}` + `fixed` +
 * `width` pins correctly with the shadow. So the many `'max-content'` tables
 * in this module keep their column sizing exactly as it is.
 *
 * @param {number} width Desired lane width at `sm` and above.
 * @param {object} [opts]
 * @param {boolean} [opts.secondary] This is the second of two pinned columns;
 *   give up the pin below `sm` rather than shrink.
 * @param {number} [opts.maxWidth] Hard ceiling for this lane at every width.
 *   For tables whose scroller is much narrower than the window.
 * @returns {object} Spread the result into a column definition. Below `sm` a
 *   `secondary` column returns `{ width }` with no `fixed`, which is safe: the
 *   fixed-left run still starts at index 0 and simply becomes one column long.
 */
export default function useStickyIdentity() {
  const screens = useBreakpoint();
  // `xs` is `(max-width: 575px)` — see antd's `responsiveObserver`. On the
  // very first render the map is still empty, so this reads `undefined` and
  // the caller's full width applies until the media query reports in; that
  // settles within a frame and only ever widens, never clips.
  const narrow = screens.xs === true;

  return useCallback(
    (width, { secondary = false, maxWidth } = {}) => {
      const ceiling = Math.min(width, maxWidth ?? Infinity);
      if (!narrow) return { fixed: 'left', width: ceiling };
      if (secondary) return { width: ceiling };
      return { fixed: 'left', width: Math.min(ceiling, NARROW_MAX) };
    },
    [narrow],
  );
}
